// Compartilhado pelas rotas 'check-document-sdk' e 'liveness' — quando o
// liveness já foi feito no outro fluxo (PF/PJ do mesmo CPF), pula direto pra
// tela de confirmação final em vez de mostrar a instrução de QR Code. Import
// dinâmico (não no topo do arquivo) para manter esse módulo de rotas livre de
// efeitos colaterais no load, igual aos demais `component: () => import(...)`
// da lista. Só indicator/type são encaminhados (inofensivos) — cpf/otp/cnpj
// nunca devem trafegar na URL; `checkExistingLiveness`/`getLivenessIdentityPayload`
// já resolvem esses dados via store (pfStore/pjStore/resendStore).
const redirectIfLivenessAlreadyDone = async (to, from, next) => {
  try {
    const { checkExistingLiveness } = await import('@/utils/onboardingLivenessCheck')
    const alreadyDone = await checkExistingLiveness(to)
    next(
      alreadyDone
        ? {
            path: '/onboarding/finalize',
            query: { indicator: to.query.indicator, type: to.query.type },
          }
        : undefined,
    )
  } catch {
    next()
  }
}

const routes = [
  {
    path: '/onboarding/',
    component: () => import('@/view/Layouts/OnboardingContainer/OnboardingContainer.vue'),
    children: [
      {
        name: 'Início do Onboarding',
        path: 'start',
        component: () => import('@/view/pages/shared/onboarding/components/OnboardingStart.vue'),
      },
      {
        path: 'instructions',
        component: () => import('@/view/pages/shared/onboarding/pages/OnboardingInstructions.vue'),
        name: 'Instruções do Onboarding',
      },

      {
        path: 'person',
        component: () => import('@/view/pages/shared/onboarding/pages/CpfPage.vue'),
      },
      {
        path: 'init',
        component: () => import('@/view/pages/shared/onboarding/pages/EmailTelephonePage.vue'),
      },
      {
        path: 'email-mfa',
        component: () => import('@/view/pages/shared/onboarding/pages/EmailMfaPage.vue'),
      },
      {
        path: 'check-document-sdk',
        component: () => import('@/view/pages/shared/onboarding/pages/CheckDocumentsToSDK.vue'),
        beforeEnter: redirectIfLivenessAlreadyDone,
      },
      {
        path: 'attachments',
        component: () => import('@/view/pages/shared/onboarding/pages/AttachmentsPage.vue'),
      },
      {
        path: 'liveness',
        component: () => import('@/view/pages/shared/onboarding/pages/LivenessPage.vue'),
        // Rede de segurança pra quem chegar direto em '/onboarding/liveness'
        // (ex.: QR Code já gerado antes desta mudança) — o fluxo normal já é
        // desviado um passo antes, no guard de 'check-document-sdk'. Nada é
        // submetido aqui: quem confirma e finaliza é o usuário, na tela de
        // destino.
        beforeEnter: redirectIfLivenessAlreadyDone,
      },

      {
        path: 'personal',
        component: () => import('@/view/pages/shared/onboarding/pages/PersonalDataPage.vue'),
      },

      {
        path: 'address',
        component: () => import('@/view/pages/shared/onboarding/pages/AddressPage.vue'),
      },

      {
        path: 'review',
        component: () => import('@/view/pages/shared/onboarding/pages/ReviewInstructionsPage.vue'),
      },

      {
        path: 'finalize',
        component: () =>
          import('@/view/pages/shared/onboarding/pages/FinalizeRegistrationPage.vue'),
      },

      {
        path: 'first-access',
        component: () => import('@/view/pages/shared/onboarding/pages/FirstAccessPage.vue'),
      },

      {
        name: 'Validação de Email - Cadastro de Senhas',
        path: 'mail-first-access',
        component: () => import('@/view/pages/shared/onboarding/pages/OTPFirstAccessPage.vue'),
      },

      {
        name: 'Instruções de Cadastro de Senhas',
        path: 'password-instructions',
        component: () =>
          import('@/view/pages/shared/onboarding/pages/PasswordInstructionsPage.vue'),
      },

      {
        name: 'Cadastro de Senha de Acesso',
        path: 'set-password',
        component: () => import('@/view/pages/shared/onboarding/pages/SetPassword.vue'),
      },
      {
        name: 'Cadastro de Senha de Autorização',
        path: 'set-signature-password',
        component: () => import('@/view/pages/shared/onboarding/pages/SetSignaturePassword.vue'),
      },

      {
        name: 'Sucesso no Cadastro de Senhas',
        path: 'passwords-success',
        component: () => import('@/view/pages/shared/onboarding/pages/PasswordsSuccessPage.vue'),
      },

      {
        path: 'company',
        component: () => import('@/view/pages/shared/onboarding/pages/company/Cpf&CnpjPage.vue'),
      },
      {
        path: 'company-init',
        component: () => import('@/view/pages/shared/onboarding/pages/EmailTelephonePage.vue'),
      },
      {
        path: 'company-data',
        component: () => import('@/view/pages/shared/onboarding/pages/company/CompanyDataPage.vue'),
      },
      {
        path: 'company-attachments',
        component: () => import('@/view/pages/shared/onboarding/pages/AttachmentsPage.vue'),
      },
      {
        path: 'request-cpf',
        component: () => import('@/view/pages/shared/onboarding/pages/CpfInputPage.vue'),
      },
      {
        path: 'request-otp',
        component: () => import('@/view/pages/shared/onboarding/pages/RequestOtpPage.vue'),
      },
      {
        path: 'list-requests',
        component: () => import('@/view/pages/shared/onboarding/pages/ListRequestsPage.vue'),
      },
      {
        path: 'resend-documents',
        component: () => import('@/view/pages/shared/onboarding/pages/ResendDocumentsPage.vue'),
      },
      {
        path: 'resend/personal-data',
        component: () => import('@/view/pages/shared/onboarding/pages/ResendPersonalDataPage.vue'),
      },
      {
        path: 'resend/address',
        component: () => import('@/view/pages/shared/onboarding/pages/ResendAddressPage.vue'),
      },
      {
        path: 'resend/company-data',
        component: () => import('@/view/pages/shared/onboarding/pages/ResendCompanyDataPage.vue'),
      },
      {
        path: 'resend/company-document',
        component: () =>
          import('@/view/pages/shared/onboarding/pages/ResendCompanyDocumentPage.vue'),
      },
    ],
  },
  {
    path: '/onboarding/liveness-sdk',
    component: () => import('@/view/pages/shared/onboarding/pages/LivenessSDKPage.vue'),
  },

  {
    path: '/onboarding/waiting',
    component: () => import('@/view/pages/shared/onboarding/pages/WaitingPage.vue'),
  },
  {
    path: '/onboarding/failed',
    component: () => import('@/view/pages/shared/onboarding/pages/FailedPage.vue'),
  },
  {
    path: '/onboarding/success',
    component: () => import('@/view/pages/shared/onboarding/pages/SuccessPage.vue'),
  },
]
export default routes
